import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Skeleton, Tag } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { InvestorProfile, PlanData, getVisualizationData } from '@/api/custom';
import { getInvestorDisplayName } from '@/util/investor-display';
import './index.scss';

type FetchState = 'idle' | 'loading' | 'ready' | 'failed';

interface PlanningProps {
  user?: InvestorProfile | null;
}

const extractPlanName = (fileName: string): string => {
  const nameParts = fileName.split('_');
  return nameParts[1] || 'Financial plan';
};

export const financialPlanTitle = (plan: PlanData): string =>
  plan.title && plan.title !== 'Untitled'
    ? plan.title
    : extractPlanName(plan.visualizationHtml.fileName);

export const formatPlanSharedDate = (timestamp: number): string =>
  dayjs(timestamp * 1000).format('MMM D, YYYY');

const firstNameForInvestor = (investor: InvestorProfile): string =>
  getInvestorDisplayName(investor).split(/\s+/)[0] || getInvestorDisplayName(investor);

const Planning: React.FC<PlanningProps> = ({ user }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [fetchState, setFetchState] = useState<FetchState>('idle');
  const cognitoId = user?.cognito_id?.trim();
  const firstName = user ? firstNameForInvestor(user) : 'this client';

  const loadPlans = useCallback(async () => {
    if (!cognitoId) {
      setPlans([]);
      setFetchState('ready');
      return;
    }

    setFetchState('loading');
    try {
      const response = await getVisualizationData(cognitoId);
      setPlans(Array.isArray(response) ? response : []);
      setFetchState('ready');
    } catch (error) {
      console.error('Failed to load financial plans:', error);
      setFetchState('failed');
    }
  }, [cognitoId]);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  const sortedPlans = useMemo(() => [...plans].sort((a, b) => b.sharedAt - a.sharedAt), [plans]);

  if (fetchState === 'idle' || fetchState === 'loading') {
    return (
      <div className="financial-plan-list-page" aria-busy="true">
        <div className="financial-plan-list">
          {[0, 1, 2].map((index) => (
            <div className="financial-plan-list-row is-loading" key={index}>
              <Skeleton.Input active size="small" className="financial-plan-skeleton-title" />
              <Skeleton.Input active size="small" className="financial-plan-skeleton-meta" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (fetchState === 'failed') {
    return (
      <div className="financial-plan-list-page">
        <Alert
          type="warning"
          showIcon
          message={`Couldn't load ${firstName}'s plans`}
          description="The plans are safe, but the service did not respond. Try again in a moment."
          action={
            <Button type="primary" onClick={() => void loadPlans()}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  if (sortedPlans.length === 0) {
    return (
      <div className="financial-plan-list-page">
        <section className="financial-plan-empty-state" aria-label="No financial plans">
          <h2>No plan shared with {firstName} yet</h2>
          <p>
            Plans shared from Wavvest Planning will appear here as a versioned list, so advisors can
            reopen exactly what the client saw.
          </p>
          <Button type="primary" onClick={() => navigate('/home/planning')}>
            Open Wavvest Planning
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="financial-plan-list-page">
      <div className="financial-plan-list">
        {sortedPlans.map((plan, index) => (
          <a
            key={plan.id}
            className="financial-plan-list-row"
            href={`/home/clients/${encodeURIComponent(user?.id ?? '')}/plan/${encodeURIComponent(
              plan.id,
            )}`}
            target="_blank"
            rel="noopener"
          >
            <span className="financial-plan-list-row-main">
              <span className="financial-plan-list-row-title">{financialPlanTitle(plan)}</span>
              <span className="financial-plan-list-row-meta">
                Shared {formatPlanSharedDate(plan.sharedAt)}
                {plan.sharedBy ? ` by ${plan.sharedBy}` : ''}
              </span>
            </span>
            {index === 0 ? (
              <Tag className="financial-plan-latest-tag" bordered={false}>
                Latest
              </Tag>
            ) : null}
            <ExportOutlined aria-label="Opens in a new tab" />
          </a>
        ))}
      </div>
    </div>
  );
};

export default Planning;
